import { REGEX_ALPHANUMERIC, validateEmail, validatePassword, validateUsername } from 'common/api/v1/helpers'
import { Address, IpPortMode, OccupiedPort } from 'common/api/v1/types'
import {
  getLocalPortNumbersUsedByInputProtocol,
  getLocalPortNumbersUsedByOutputProtocol,
  isTcpBasedProtocol,
  isUdpBasedProtocol,
  MAX_PORT_NUMBER,
  TCP_EDGE_CONTROL_HTTP_PORT,
  UDP_HANDOVER_PORT_RANGE_END,
  UDP_HANDOVER_PORT_RANGE_START,
  UDP_TUNNEL_PORT_RANGE_END,
  UDP_TUNNEL_PORT_RANGE_START,
} from 'common/ports'
import { getOverlappingCidrBlocks } from 'common/addrv4'
import { parseK8sMemoryString } from 'common/utils'
import { findClosestValue } from '.'

/** Returns true if value is empty (undefined, null, or empty string) */
const isEmpty = (value: unknown): value is undefined | null | '' =>
  value === '' || value === undefined || value === null

/**
 * Individual validator implementations
 * Each returns undefined for valid, or an error message string for invalid
 */
const validators = {
  username: (value: unknown) => {
    return validateUsername(value as string)
  },

  pwd: (value: unknown) => {
    return validatePassword(value as string)
  },

  presence: (value: unknown, constraints: unknown) => {
    const isEmptyValue =
      value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0)
    if (isEmptyValue) {
      if (typeof constraints === 'string') return constraints
      return "Can't be blank"
    }
    return undefined
  },

  email: (value: unknown) => {
    if (isEmpty(value)) return undefined
    return validateEmail(value as string)
  },

  number: (value: unknown, constraints: Record<string, unknown>) => {
    if (isEmpty(value)) return undefined

    const num = typeof value === 'string' ? parseFloat(value) : (value as number)
    if (isNaN(num)) return 'is not a number'

    const { greaterThanOrEqualTo, lessThanOrEqualTo, greaterThan, lessThan, even, divisibleBy, message } = constraints

    if (greaterThanOrEqualTo !== undefined && num < (greaterThanOrEqualTo as number)) {
      return (message as string) || `must be greater than or equal to ${greaterThanOrEqualTo}`
    }
    if (lessThanOrEqualTo !== undefined && num > (lessThanOrEqualTo as number)) {
      return (message as string) || `must be less than or equal to ${lessThanOrEqualTo}`
    }
    if (greaterThan !== undefined && num <= (greaterThan as number)) {
      return (message as string) || `must be greater than ${greaterThan}`
    }
    if (lessThan !== undefined && num >= (lessThan as number)) {
      return (message as string) || `must be less than ${lessThan}`
    }
    if (even && num % 2 !== 0) {
      return (message as string) || 'must be even'
    }
    if (divisibleBy !== undefined && num % (divisibleBy as number) !== 0) {
      return (message as string) || `must be divisible by ${divisibleBy}`
    }

    return undefined
  },

  integers: (input: unknown) => {
    if (isEmpty(input)) return undefined
    const values: unknown[] = Array.isArray(input) ? input : [input]
    const errors = values.filter((v) => !Number.isInteger(Number(v))).map((v) => `${v} must be an integer`)
    if (errors.length > 0) {
      return errors.join(', ')
    }
    return undefined
  },

  pid: (value: unknown) => {
    if (isEmpty(value)) return undefined

    const num = typeof value === 'string' ? parseInt(value, 10) : (value as number)
    const MIN = 32 // 1-31 are reserved
    const MAX = 8190 // 8191 (0x1FFF) is Null packets
    const RESERVED = [8187] // 0x1FFB. See https://en.wikipedia.org/wiki/MPEG_transport_stream

    if (isNaN(num) || num < MIN || num > MAX) {
      return `Must be between ${MIN} - ${MAX}`
    }
    if (RESERVED.includes(num)) {
      return `${num} is reserved`
    }
    return undefined
  },

  ip: (value: unknown) => {
    if (isEmpty(value)) return undefined
    const str = String(value)
    if (!str.match(/^(?:(?:^|\.)(?:2(?:5[0-5]|[0-4]\d)|1?\d?\d)){4}$/)) {
      return 'Must be valid IPv4 address'
    }
    return undefined
  },

  multicastAddress: (value: unknown) => {
    if (isEmpty(value)) return undefined
    const str = String(value)
    const ipError = validators.ip(str)
    const firstPart = parseInt(str.split('.')[0], 10)
    if (ipError || !(224 <= firstPart && firstPart <= 239)) {
      return 'Multicast address must be in range 224.0.0.0/4 (224.0.0.0 to 239.255.255.255)'
    }
    return undefined
  },

  ipv4CidrBlock: (inputValue: unknown) => {
    if (isEmpty(inputValue)) return undefined

    const cidrBlocks = Array.isArray(inputValue) ? (inputValue as string[]) : String(inputValue).split(',')

    for (const cidr of cidrBlocks) {
      if (
        !cidr.match(
          /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)(\/([0-9]|[1-2][0-9]|3[0-2]))$/,
        )
      ) {
        return `${cidr} is not a valid CIDR block`
      }
    }

    const overlapping = getOverlappingCidrBlocks(cidrBlocks)
    if (overlapping) {
      return `${overlapping.cidr2.toString()} overlaps with ${overlapping.cidr1.toString()}`
    }

    return undefined
  },

  format: (value: unknown, constraints: { prefixes?: string[] }) => {
    if (isEmpty(value)) return undefined
    const str = String(value)
    if (constraints.prefixes) {
      const match = constraints.prefixes.find((prefix) => str.startsWith(prefix))
      if (!match) {
        return `Must start with ${constraints.prefixes.join(' or ')}`
      }
    }
    return undefined
  },

  /**
   * Validates URL format
   * @param value - URL to validate
   * @param noProtocol - If true, protocol (https://) is not allowed. Default: false
   * @param noSubpath - If true, subpaths (/path) are not allowed. Default: false
   */
  url: (value: unknown, { noProtocol = false, noSubpath = false }: { noProtocol?: boolean; noSubpath?: boolean }) => {
    if (isEmpty(value)) return undefined

    const str = String(value)
    if (noProtocol) {
      if (str.includes('://')) return 'Must not contain protocol'
      if (noSubpath && str.includes('/')) return 'Must not contain subpath'
    } else {
      try {
        const { pathname } = new URL(str)
        if (noSubpath && pathname && pathname !== '/') return 'Must not contain subpath'
      } catch {
        return 'Must be a valid URL'
      }
    }
    return undefined
  },

  ipOrHostname: (value: unknown) => {
    if (isEmpty(value)) return undefined

    const str = String(value)
    const validIpPattern = /^(?:(?:^|\.)(?:2(?:5[0-5]|[0-4]\d)|1?\d?\d)){4}$/
    // If it looks like an IP address (4 dot-separated digit groups), validate strictly as IPv4
    const looksLikeIpPattern = /^\d{1,3}(\.\d{1,3}){3}$/
    if (looksLikeIpPattern.test(str)) {
      return validIpPattern.test(str) ? undefined : 'Must be valid IPv4 address or FQDN'
    }
    const validHostnamePattern =
      /^(([a-zA-Z0-9]|[a-zA-Z0-9][a-zA-Z0-9-]*[a-zA-Z0-9])\.)*([A-Za-z0-9]|[A-Za-z0-9][A-Za-z0-9-]*[A-Za-z0-9])$/
    if (!validHostnamePattern.test(str)) {
      return 'Must be valid IPv4 address or FQDN'
    }
    return undefined
  },

  port: (
    value: unknown,
    constraints: {
      isUdp: boolean
      even?: boolean
      allowedInternalRanges?: {
        tunnel?: true
        handover?: true
        ecHttpControl?: true
      }
    },
  ) => {
    if (isEmpty(value)) return undefined

    const str = String(value)
    if (!str.match(/^[0-9]+$/)) return 'Must be a valid port number'

    const MAX = 65535
    const MIN = 1024
    const port = parseInt(str, 10)

    const even = constraints?.even
    const evenMessage = even ? 'an even number in the range ' : ''

    if (port < MIN || port > MAX) {
      return `Must be ${evenMessage}${MIN} - ${MAX}`
    }
    if (even && port % 2 !== 0) {
      return `Must be ${evenMessage}${MIN} - ${MAX}`
    }

    if (
      !constraints.allowedInternalRanges?.tunnel &&
      constraints.isUdp &&
      UDP_TUNNEL_PORT_RANGE_START <= port &&
      port <= UDP_TUNNEL_PORT_RANGE_END
    ) {
      return `UDP ports ${UDP_TUNNEL_PORT_RANGE_START} to ${UDP_TUNNEL_PORT_RANGE_END} are reserved for internal use`
    }

    if (
      !constraints.allowedInternalRanges?.handover &&
      constraints.isUdp &&
      UDP_HANDOVER_PORT_RANGE_START <= port &&
      port <= UDP_HANDOVER_PORT_RANGE_END
    ) {
      return `UDP ports ${UDP_HANDOVER_PORT_RANGE_START} to ${UDP_HANDOVER_PORT_RANGE_END} are reserved for internal use`
    }

    if (
      !constraints.allowedInternalRanges?.ecHttpControl &&
      !constraints.isUdp &&
      port === TCP_EDGE_CONTROL_HTTP_PORT
    ) {
      return `TCP port ${TCP_EDGE_CONTROL_HTTP_PORT} is reserved for internal use`
    }

    return undefined
  },

  len: (value: unknown, constraints: { minimum?: number; maximum?: number; tooShort?: string; tooLong?: string }) => {
    if (isEmpty(value)) return undefined

    const str = String(value)
    if (constraints.minimum && str.length < constraints.minimum) {
      return constraints.tooShort || `Must be at least ${constraints.minimum} characters long`
    }
    if (constraints.maximum && str.length > constraints.maximum) {
      return constraints.tooLong || `Must be no more than ${constraints.maximum} characters long`
    }
    return undefined
  },

  hexadecimal: (value: unknown) => {
    if (isEmpty(value)) return undefined
    if (!String(value).match(/^[\d\sa-fA-F]+$/)) return 'Must be valid hexadecimal'
    return undefined
  },

  alphanumeric: (value: unknown) => {
    if (isEmpty(value)) return undefined
    if (!String(value).match(REGEX_ALPHANUMERIC)) return 'Must be alphanumeric'
    return undefined
  },

  noAmpersand: (value: unknown) => {
    if (isEmpty(value)) return undefined
    if (String(value).includes('&')) return 'Must not contain ampersand (&)'
    return undefined
  },

  addressIn: (value: unknown, { addresses }: { addresses: Address[] }) => {
    const validAddresses = (addresses || []).reduce((acc, address) => {
      acc.add(address.address)
      address.publicAddress && acc.add(address.publicAddress)
      address.internalAddress && acc.add(address.internalAddress)
      address.interRegionPublicAddress && acc.add(address.interRegionPublicAddress)
      return acc
    }, new Set<string>())

    if (!validAddresses.has(value as string)) {
      const formattedValidAddresses = Array.from(validAddresses)
        .map((address) => `'${address || 'any'}'`)
        .sort()
      return `Must be ${formattedValidAddresses.join(' or ')}`
    }
    return undefined
  },

  k8sMemoryString: (value: unknown, { min, max, message }: { min?: string; max?: string; message?: string }) => {
    if (isEmpty(value)) return undefined

    const parsed = parseK8sMemoryString(String(value))
    if (Number.isNaN(parsed)) {
      return 'Invalid value. Supports suffixes like G, M, Gi and Mi'
    }

    if (min !== undefined) {
      const parsedMin = parseK8sMemoryString(min)
      if (Number.isNaN(parsedMin)) {
        return 'Invalid minimum value'
      }
      if (parsed < parsedMin) {
        return message || `Must be at least ${min}`
      }
    }

    if (max !== undefined) {
      const parsedMax = parseK8sMemoryString(max)
      if (Number.isNaN(parsedMax)) {
        return 'Invalid maximum value'
      }
      if (parsed > parsedMax) {
        return message || `Must be at most ${max}`
      }
    }
    return undefined
  },

  k8sName: (value: unknown) => {
    if (isEmpty(value)) return undefined

    const str = String(value)
    if (str.startsWith('-') || str.endsWith('-')) return 'Name cannot start or end with hyphen'
    if (!str.match(/^[a-z0-9-]+$/)) return 'Only lowercase letters, numbers and hyphens are allowed'
    return undefined
  },

  oneOf: (value: unknown, { validValues }: { validValues: Set<string> }) => {
    if (validValues.size > 0 && !validValues.has(value as string)) {
      const prefix = `Must be ${validValues.size === 1 ? '' : 'one of:'}`
      return `${prefix} ${Array.from(validValues).join(', ')}`
    }
    return undefined
  },

  notOneOf: (
    value: unknown,
    { invalidValues, errorMsg }: { invalidValues: Set<string | number>; errorMsg: string },
  ) => {
    if (value && invalidValues.has(value as string | number)) {
      return errorMsg
    }
    return undefined
  },

  isPortAvailable: (value: unknown, portInfo: PortToValidate) => {
    if (portInfo.isMulticast) return undefined
    if (portInfo.isPortDisabled) return undefined
    const numericValue = Number(value)
    if (!value || !numericValue || isNaN(numericValue)) return undefined

    const isRtcp = portInfo.mode === IpPortMode.rist || (portInfo.mode === IpPortMode.rtp && !!portInfo.isRtpRtcp)
    const requestedLocalPorts: number[] = []
    if (portInfo.type === 'input') {
      requestedLocalPorts.push(
        ...getLocalPortNumbersUsedByInputProtocol({
          ...portInfo,
          mode: portInfo.mode as IpPortMode,
          localBasePort: numericValue,
          isFec: portInfo.isFec ?? false,
          isRtcp,
          isMulticast: portInfo.isMulticast ?? false,
        }),
      )
    } else if (portInfo.type === 'output') {
      requestedLocalPorts.push(
        ...getLocalPortNumbersUsedByOutputProtocol({
          ...portInfo,
          mode: portInfo.mode as IpPortMode,
          localBasePort: numericValue,
        }),
      )
    } else {
      requestedLocalPorts.push(numericValue)
    }

    // EDGE-6632: RTP's derived ports (RTCP +1, FEC +2/+4) must also stay within range.
    const outOfRangePort = requestedLocalPorts.find((p) => p > MAX_PORT_NUMBER)
    if (outOfRangePort !== undefined) {
      return `Port ${outOfRangePort} is out of range, the maximum allowed port is ${MAX_PORT_NUMBER}${
        portInfo.isFec ? ' (FEC requires the base port + 2 and + 4)' : ''
      }`
    }

    const isUDP = isUdpBasedProtocol(portInfo.mode)
    const potentiallyConflictingPorts = portInfo.existingLogicalPortsOnSameNic.filter(
      (lp) => !lp.portMode || (isUDP ? isUdpBasedProtocol(lp.portMode) : isTcpBasedProtocol(portInfo.mode)),
    )
    for (const rp of requestedLocalPorts) {
      const portOccupant = potentiallyConflictingPorts.find((o) => o.localPortNumbers.includes(rp))
      if (portOccupant) {
        if (!portOccupant.portMode) {
          return `${isUDP ? 'UDP' : 'TCP'} port ${rp} on the selected interface is already in use.`
        }
        if (portOccupant.portMode === 'tunnel-client' || portOccupant.portMode === 'tunnel-server') {
          return `UDP port ${rp} on the selected interface is already in use by tunnel ${portOccupant.tunnelId}`
        }
        return `${
          isUDP ? 'UDP' : 'TCP'
        } port ${rp} on the selected interface is already in use by a ${portOccupant.portMode.toUpperCase()}-${
          portOccupant.inputId ? 'input' : portOccupant.outputId ? 'output' : 'service'
        }, but required by the selected protocol.`
      }
    }
    return undefined
  },

  audioCodecBitrate: (
    value: unknown,
    {
      codec,
      supportedBitratesInKbps,
      maxBitrate,
    }: {
      codec: 'aac' | 'mp2' | 'ac3'
      supportedBitratesInKbps: number[]
      maxBitrate: number
    },
  ) => {
    if (isEmpty(value)) return undefined

    const bitrate = typeof value === 'string' ? parseFloat(value) : (value as number)

    if (supportedBitratesInKbps.length > 0 && !supportedBitratesInKbps.includes(bitrate)) {
      const closest = findClosestValue(bitrate, supportedBitratesInKbps)!
      return `${codec} does not support bitrate ${bitrate} kbps. Closest supported bitrate is ${closest} kbps.`
    }

    if (bitrate <= 0 || bitrate > maxBitrate) {
      return `Must be greater than 0 and no more than ${maxBitrate}`
    }

    return undefined
  },
} as const

export interface PortToValidate {
  type: 'input' | 'output' | 'tunnel'
  isPortDisabled: boolean
  mode: IpPortMode | 'tunnel-client' | 'tunnel-server'
  existingLogicalPortsOnSameNic: OccupiedPort[]
  isFec?: boolean
  isRtpRtcp?: boolean
  isMulticast?: boolean
}

/** Empty object type for validators that take no options */
type NoOptions = Record<string, never>

/**
 * Constraint types for the validate function
 */
export interface ValidatorConstraints {
  presence?: boolean | string | { allowEmpty?: boolean }
  email?: NoOptions
  username?: NoOptions
  pwd?: NoOptions
  number?: {
    greaterThanOrEqualTo?: number
    lessThanOrEqualTo?: number
    greaterThan?: number
    lessThan?: number
    even?: boolean
    divisibleBy?: number
    onlyInteger?: boolean
    noStrings?: boolean
    message?: string
  }
  integers?: NoOptions
  pid?: NoOptions
  ip?: NoOptions
  multicastAddress?: NoOptions
  ipv4CidrBlock?: NoOptions
  format?: { prefixes?: string[] }
  url?: { noProtocol?: boolean; noSubpath?: boolean }
  ipOrHostname?: NoOptions
  port?: {
    isUdp: boolean
    even?: boolean
    allowedInternalRanges?: {
      tunnel?: boolean
      handover?: boolean
      ecHttpControl?: boolean
    }
  }
  len?: { minimum?: number; maximum?: number; tooShort?: string; tooLong?: string }
  hexadecimal?: NoOptions
  alphanumeric?: NoOptions
  noAmpersand?: NoOptions
  addressIn?: { addresses: Address[] }
  k8sMemoryString?: { min?: string; max?: string; message?: string }
  k8sName?: NoOptions
  oneOf?: { validValues: Set<string | number | undefined> }
  notOneOf?: { invalidValues: Set<string | number>; errorMsg: string }
  isPortAvailable?: PortToValidate
  audioCodecBitrate?: {
    codec: 'aac' | 'mp2' | 'ac3'
    supportedBitratesInKbps: number[]
    maxBitrate: number
  }
  /** Custom validator function for dynamic validation */
  custom?: (value: unknown) => string | undefined
  [key: string]: unknown
}

/**
 * Creates a validator function for React Hook Form from a constraints object
 * @param constraints - Object with validator names as keys and their options as values
 * @returns A validator function that returns undefined for valid values or an error message string
 */
export const validate =
  (constraints: ValidatorConstraints) =>
  (value?: unknown): string | undefined => {
    // Check for leading/trailing spaces first
    if (typeof value === 'string' && value.match(/(^\s|\s$)/)) {
      return 'Leading and trailing spaces are not allowed.'
    }

    const errors: string[] = []

    for (const [key, constraintValue] of Object.entries(constraints)) {
      if (constraintValue === undefined || constraintValue === false) continue

      // Handle custom validator function
      if (key === 'custom' && typeof constraintValue === 'function') {
        const error = constraintValue(value)
        if (error) errors.push(error)
        continue
      }

      if (key in validators) {
        const validator = validators[key as keyof typeof validators]
        const constraintOptions =
          typeof constraintValue === 'boolean' || typeof constraintValue === 'string' ? {} : constraintValue
        const error = validator(value, constraintOptions as never)
        if (error) {
          errors.push(error)
        }
      }
    }

    return errors.length > 0 ? errors.join(', ') : undefined
  }

/**
 * Export individual validators for direct use or testing
 */
export { validators }
